/**
 * session.service.ts
 * ------------------
 * Tracks the current assessment flow mode (new vs existing), the
 * completion state of each step, and the session ID returned by the
 * backend after a successful Module 4 document upload.
 *
 * Key behaviours:
 *  - All state is stored in sessionStorage so each browser tab has its own
 *    independent session (feature #4: multiple tabs = multiple sessions).
 *  - An inactivity timer resets on every user interaction. When 10 minutes
 *    elapse without activity the session is expired, the backend session
 *    folder is deleted, and the user is redirected to the landing page
 *    (feature #1).
 *  - A heartbeat ping is sent to the backend every 3 minutes while a
 *    session is active so the server-side cleanup task knows the session
 *    is still alive.
 *  - On page reload, if a previous sessionId exists in sessionStorage it
 *    is sent to the backend to be deleted before a new one is created
 *    (feature #2).
 */

import { Injectable, signal, computed, OnDestroy, NgZone } from '@angular/core';
import { HttpClient }                                       from '@angular/common/http';
import { Observable, of }                                   from 'rxjs';
import { catchError }                                       from 'rxjs/operators';
import { Router }                                           from '@angular/router';
import { environment }                                      from '../../../environments/environment';

/** Possible flow modes — drives which stepper steps are shown */
export type FlowMode = 'new' | 'existing' | null;

/**
 * Step keys used as identifiers in both the stepper and the step-guard.
 * New assessment  : document-upload → category-review → assessment-report
 * Existing        : application-details → assessment-report
 */
export type StepKey =
  | 'document-upload'
  | 'application-details'
  | 'category-review'
  | 'assessment-report';

// sessionStorage keys — prefixed to avoid collisions
const SK_FLOW_MODE    = 'cat_flow_mode';
const SK_SESSION_ID   = 'cat_session_id';
const SK_COMPLETED    = 'cat_completed_steps';
/** Stores the finalized (Module 8) selected category names as a JSON array. */
const SK_FINAL_CATS   = 'cat_finalized_categories';

// localStorage key — survives page reload, used to delete session after reload
const LK_PENDING_DELETE = 'cat_pending_delete_session_id';

// Inactivity timeout: 30 minutes in milliseconds
const INACTIVITY_MS = 30 * 60 * 1000;

// Warning shown 30 seconds before timeout
const WARNING_BEFORE_MS = 30 * 1000;

// Extension on "Extend Session": 30 full minutes
const EXTEND_MS = 30 * 60 * 1000;

// Heartbeat interval: every 3 minutes
const HEARTBEAT_MS  =  3 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SessionService implements OnDestroy {

  private readonly _baseUrl =
    `${environment.apiConfig.uri.replace(/\/$/, '')}/api/v1/sessions`;

  // ── Flow mode ─────────────────────────────────────────────────────────
  private readonly _flowMode = signal<FlowMode>(
    (sessionStorage.getItem(SK_FLOW_MODE) as FlowMode) ?? null
  );
  readonly flowMode = this._flowMode.asReadonly();

  // ── Step completion ───────────────────────────────────────────────────
  private readonly _completed = signal<Set<StepKey>>(
    this._loadCompletedSteps()
  );
  readonly completedSteps = computed(() => new Set(this._completed()));

  // ── Backend session ID ────────────────────────────────────────────────
  private readonly _sessionId = signal<string | null>(
    sessionStorage.getItem(SK_SESSION_ID)
  );
  readonly sessionId = this._sessionId.asReadonly();

  // ── Timers ────────────────────────────────────────────────────────────
  private _inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private _heartbeatTimer:  ReturnType<typeof setInterval> | null = null;
  private _warningTimer:    ReturnType<typeof setTimeout> | null = null;
  private _postWarnTimer:   ReturnType<typeof setTimeout> | null = null;

  // ── Timeout warning modal state ───────────────────────────────────────
  /** True while the "Session expiring soon" warning modal is visible. */
  readonly showTimeoutWarning = signal<boolean>(false);

  /** True when the session has fully expired — shows the full-page expired screen. */
  readonly showSessionExpired = signal<boolean>(false);

  constructor(
    private readonly http:   HttpClient,
    private readonly router: Router,
    private readonly zone:   NgZone,
  ) {
    // If a sessionId is already in sessionStorage (e.g. mid-flow on reload)
    // do NOT auto-restore: the LandingComponent will delete it before starting
    // a new session. We do start the timers if a session is active.
    if (this._sessionId()) {
      this._startTimers();
    }

    // Listen to user activity events to reset the inactivity clock
    this._bindActivityListeners();

    // Feature #2: When the tab is about to be closed or the page refreshed,
    // show a confirmation dialog and — if confirmed — delete the backend session
    // via sendBeacon (fires even while the page is unloading).
    window.addEventListener('beforeunload', this._onBeforeUnload);
  }

  ngOnDestroy(): void {
    this._clearTimers();
    window.removeEventListener('beforeunload', this._onBeforeUnload);
  }

  // ── Public API ────────────────────────────────────────────────────────

  startNewAssessment(): void {
    this._flowMode.set('new');
    this._completed.set(new Set());
    this._sessionId.set(null);
    this._persist();
  }

  startExistingAssessment(): void {
    this._flowMode.set('existing');
    this._completed.set(new Set());
    this._sessionId.set(null);
    this._persist();
  }

  markStepComplete(step: StepKey): void {
    this._completed.update((set) => {
      const next = new Set(set);
      next.add(step);
      return next;
    });
    this._persist();
  }

  isStepComplete(step: StepKey): boolean {
    return this._completed().has(step);
  }

  // ── Module 8: Finalized category names ───────────────────────────────────

  /**
   * Persist the final set of selected category names chosen on the
   * Category Selection page (Module 8).  These are read back by the
   * Assessment Report page (Module 10+).
   */
  setFinalizedCategories(names: string[]): void {
    sessionStorage.setItem(SK_FINAL_CATS, JSON.stringify(names));
  }

  /** Return the finalized category names saved by Module 8, or [] if none. */
  getFinalizedCategories(): string[] {
    try {
      const raw = sessionStorage.getItem(SK_FINAL_CATS);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }

  /**
   * Called by DocumentUploadComponent after a successful backend response.
   * Stores the UUID returned by POST /api/v1/sessions/upload and starts
   * the inactivity & heartbeat timers.
   */
  setSessionId(id: string): void {
    this._sessionId.set(id);
    sessionStorage.setItem(SK_SESSION_ID, id);
    this._startTimers();
  }

  /**
   * Returns the session_id that was previously saved in this tab, if any.
   * Checks localStorage first (set on page unload/reload) then sessionStorage.
   * Clears the localStorage entry after reading to avoid stale deletes.
   * Used by LandingComponent to delete the old session before creating a new one.
   */
  getPreviousSessionId(): string | null {
    const fromLocalStorage = localStorage.getItem(LK_PENDING_DELETE);
    if (fromLocalStorage) {
      localStorage.removeItem(LK_PENDING_DELETE);
      return fromLocalStorage;
    }
    return sessionStorage.getItem(SK_SESSION_ID);
  }

  /**
   * Resets all state and clears timers + sessionStorage.
   * Does NOT call the backend delete endpoint — callers that need to purge
   * backend data must call the UploadService directly first.
   */
  reset(): void {
    this._clearTimers();
    this._flowMode.set(null);
    this._completed.set(new Set());
    this._sessionId.set(null);
    sessionStorage.removeItem(SK_FLOW_MODE);
    sessionStorage.removeItem(SK_SESSION_ID);
    sessionStorage.removeItem(SK_COMPLETED);
  }

  /**
   * Explicitly DELETE the backend session blob folder, clear all local state,
   * and return an Observable that completes when the request finishes (or fails).
   *
   * Use this when the user intentionally starts a new assessment mid-flow so
   * the backend storage is cleaned up properly — same logic as the inactivity
   * timeout handler.
   */
  deleteAndReset(): Observable<unknown> {
    const sid = this._sessionId();
    // Clear local state immediately so the UI transitions right away
    this.reset();
    if (!sid) { return of(null); }
    return this.http
      .delete(`${this._baseUrl}/${sid}`)
      .pipe(catchError(() => of(null))); // best-effort — never throw to caller
  }

  getStepsForFlow(): { key: StepKey; label: string }[] {
    if (this._flowMode() === 'existing') {
      return [
        { key: 'application-details', label: 'Application Details' },
        { key: 'assessment-report',   label: 'Result and Analysis' },
      ];
    }
    return [
      { key: 'document-upload',   label: 'Document Upload' },
      { key: 'category-review',   label: 'Category Selection' },
      { key: 'assessment-report', label: 'Result and Analysis' },
    ];
  }

  // ── Private helpers ───────────────────────────────────────────────────

  private _persist(): void {
    const mode = this._flowMode();
    if (mode) {
      sessionStorage.setItem(SK_FLOW_MODE, mode);
    } else {
      sessionStorage.removeItem(SK_FLOW_MODE);
    }
    const completed = [...this._completed()];
    sessionStorage.setItem(SK_COMPLETED, JSON.stringify(completed));
  }

  private _loadCompletedSteps(): Set<StepKey> {
    try {
      const raw = sessionStorage.getItem(SK_COMPLETED);
      if (raw) {
        const arr = JSON.parse(raw) as StepKey[];
        return new Set(arr);
      }
    } catch { /* ignore parse errors */ }
    return new Set();
  }

  private _startTimers(): void {
    this._clearTimers();
    this.showTimeoutWarning.set(false);

    // Inactivity timer — runs outside Angular zone to avoid CD overhead
    this.zone.runOutsideAngular(() => {
      // Warning timer: fires 1 minute before the full inactivity timeout
      this._warningTimer = setTimeout(
        () => this.zone.run(() => this._onWarningTimeout()),
        INACTIVITY_MS - WARNING_BEFORE_MS,
      );

      // Full inactivity timer: fires after the warning period expires
      this._inactivityTimer = setTimeout(
        () => this.zone.run(() => this._onInactivityTimeout()),
        INACTIVITY_MS,
      );

      this._heartbeatTimer = setInterval(
        () => this._sendHeartbeat(),
        HEARTBEAT_MS,
      );
    });
  }

  private _clearTimers(): void {
    if (this._inactivityTimer !== null) {
      clearTimeout(this._inactivityTimer);
      this._inactivityTimer = null;
    }
    if (this._heartbeatTimer !== null) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
    if (this._warningTimer !== null) {
      clearTimeout(this._warningTimer);
      this._warningTimer = null;
    }
    if (this._postWarnTimer !== null) {
      clearTimeout(this._postWarnTimer);
      this._postWarnTimer = null;
    }
  }

  /** Called every time the user interacts with the page. */
  resetInactivityTimer(): void {
    if (!this._sessionId()) { return; }  // no active session — nothing to reset
    this._startTimers();  // restarts both timers from zero
  }

  private _bindActivityListeners(): void {
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    const handler = () => this.zone.runOutsideAngular(() => this.resetInactivityTimer());
    events.forEach(evt => window.addEventListener(evt, handler, { passive: true }));
  }

  /**
   * beforeunload handler — stored as an arrow-function property so the same
   * reference can be passed to both addEventListener and removeEventListener.
   *
   * Behaviour:
   *  - If there is an active session, shows the browser's "Leave site?" dialog.
   *  - On confirmation (user leaves / refreshes), fires a synchronous beacon
   *    DELETE request so the backend session folder is cleaned up even as the
   *    page unloads.  sendBeacon is used because XHR/fetch are cancelled during
   *    unload; beacon is guaranteed to be delivered.
   */
  private readonly _onBeforeUnload = (event: BeforeUnloadEvent): void => {
    const sid = this._sessionId();
    if (!sid) { return; }

    // Trigger the browser's built-in "Leave site?" confirmation dialog.
    // Modern browsers show their own message; setting returnValue enables it.
    event.preventDefault();
    event.returnValue = '';   // required for Chrome/Edge

    // Fire a best-effort DELETE via sendBeacon so the blob folder is removed
    // even if the user confirms leaving.  sendBeacon uses a POST under the hood,
    // but we point it at a dedicated endpoint that accepts POST for unload use.
    // Fall back to a synchronous keepalive fetch if sendBeacon is unavailable.
    const url = `${this._baseUrl}/${sid}/unload`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url);
    } else {
      // keepalive fetch — sent even after the page starts to unload
      fetch(url, { method: 'POST', keepalive: true })
        .catch(() => {/* best-effort */});
    }

    // Save session ID to localStorage so LandingComponent can delete it
    // after reload (sessionStorage is cleared by reset() below).
    localStorage.setItem(LK_PENDING_DELETE, sid);

    // Clear local state immediately so a fresh reload starts clean
    this.reset();
  };

  /** Fires 1 minute before the full inactivity timeout — shows warning modal. */
  private _onWarningTimeout(): void {
    if (!this._sessionId()) { return; }
    this.showTimeoutWarning.set(true);

    // Auto-expire after 1 minute if the user does not respond
    this.zone.runOutsideAngular(() => {
      this._postWarnTimer = setTimeout(
        () => this.zone.run(() => this._onInactivityTimeout()),
        WARNING_BEFORE_MS,
      );
    });
  }

  /**
   * Called when the user clicks "Extend Session" in the warning modal.
   * Dismisses the modal and adds another EXTEND_MS (15 min) to the session.
   */
  extendSession(): void {
    this.showTimeoutWarning.set(false);
    // Cancel the post-warning auto-expire timer
    if (this._postWarnTimer !== null) {
      clearTimeout(this._postWarnTimer);
      this._postWarnTimer = null;
    }
    // Restart full timers with extra EXTEND_MS grace period by temporarily
    // advancing the inactivity window
    this._clearTimers();
    this.showTimeoutWarning.set(false);
    this.zone.runOutsideAngular(() => {
      this._warningTimer = setTimeout(
        () => this.zone.run(() => this._onWarningTimeout()),
        EXTEND_MS - WARNING_BEFORE_MS,
      );
      this._inactivityTimer = setTimeout(
        () => this.zone.run(() => this._onInactivityTimeout()),
        EXTEND_MS,
      );
      this._heartbeatTimer = setInterval(
        () => this._sendHeartbeat(),
        HEARTBEAT_MS,
      );
    });
  }

  /** Fires when the user has been inactive for the full timeout period. */
  private _onInactivityTimeout(): void {
    const sid = this._sessionId();
    if (!sid) { return; }

    this.showTimeoutWarning.set(false);

    // Best-effort backend delete
    this.http.delete(`${this._baseUrl}/${sid}`).subscribe({
      error: (err) => console.warn('Session timeout delete failed:', err),
    });

    this.reset();

    // Show the full-page session-expired screen (user must click refresh)
    this.showSessionExpired.set(true);
  }

  private _sendHeartbeat(): void {
    const sid = this._sessionId();
    if (!sid) { return; }

    this.http.post<{ alive: boolean }>(`${this._baseUrl}/${sid}/heartbeat`, {})
      .subscribe({
        next: (res) => {
          if (!res.alive) {
            // Server says session expired (e.g. server restarted)
            this.zone.run(() => {
              this.reset();
              this.router.navigate(['/']);
            });
          }
        },
        error: () => { /* silent — network hiccup, will retry next interval */ },
      });
  }
}

